import { Button } from '@app/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@app/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@app/components/ui/tooltip';
import { useLanguage } from '@app/hooks/context/LanguageContext';
import { languages, LanguageCode } from '@app/types';
import { Check, ChevronDown, Globe, LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function LanguageSwitcher() {
    //FIXME
    //const { availableLanguages } = usePage<SharedData>().props;
    const availableLanguages: LanguageCode[] = ['default', 'fr'];
    const { language, setLanguage, loading } = useLanguage();

    const { t } = useTranslation();

    const transformedLanguages = availableLanguages.map((lang) => {
        const matchedLanguage = languages.find((surveyLang) => surveyLang.code === lang);
        return matchedLanguage || { code: lang, label: lang, flag: '' };
    });
    const [selected, setSelected] = useState(language);

    const handleLanguageChange = (languageCode: LanguageCode) => {
        setSelected(languageCode);
        setLanguage(languageCode);
    };

    // Find the current language object
    const currentLang = transformedLanguages.find((lang) => lang.code === selected) || languages[0];

    // If there's only one language available
    if (transformedLanguages.length <= 1) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button variant="outline" size="sm" className="h-8 gap-2">
                            <Globe className="h-4 w-4" />
                            {currentLang?.label || 'English'}
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>{t('components.language-switcher.only-one-language-available')}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    // If there are more than two languages
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-2" disabled={loading}>
                    {loading ? (
                        <>
                            <Globe className="h-4 w-4" />
                            <LoaderCircle className="h-4 w-4 animate-spin" />
                        </>
                    ) : (
                        <>
                            {currentLang.flag && <span>{currentLang.flag}</span>}
                            {currentLang?.label}
                            <ChevronDown className="h-4 w-4 opacity-50" />
                        </>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                {transformedLanguages.map((language) => (
                    <DropdownMenuItem key={language.code} onClick={() => handleLanguageChange(language.code)} className="flex items-center gap-2">
                        {language.flag && <span>{language.flag}</span>}
                        {language.label}
                        {selected === language.code && <Check className="ml-auto h-4 w-4" />}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
